import { API_ROUTES } from '@/constants/apiRoutes';
import {
  IdempotentRequest,
  UpdateEmailFields,
  UpdatePasswordFields,
  UpdatePhoneFields,
} from '@/types/common';
import {
  StaffFormEditValues,
  StaffFormValues,
  StaffForSelectItem,
  StaffListResponse,
  StaffMemberDetails,
} from '@/types/staff';
import { api } from '@/utils/api';
import { normalizePhoneNumber } from '@/utils/string';

export const getStaffList = async (queryParams: string) => {
  const { data } = await api<StaffListResponse>({
    url: `${API_ROUTES.STAFF}?${queryParams}`,
    method: 'get',
    version: 2,
  });

  return data.staffs;
};

export const getStaffDetails = async (uuid: string) => {
  const { data } = await api<StaffMemberDetails>({
    url: `${API_ROUTES.STAFF}${uuid}`,
    method: 'get',
    version: 2,
  });

  return data;
};

export const getStaffListForSelectionService = async () => {
  const { data } = await api<StaffForSelectItem[]>({
    url: `${API_ROUTES.STAFF}for-selection`,
    method: 'get',
    version: 2,
  });

  return data;
};

function createStaffFormPayload(payload: StaffFormValues) {
  return {
    first_name: payload.firstName,
    last_name: payload.lastName,
    title: payload.title,
    email: payload.email,
    phone_number: normalizePhoneNumber(payload.phoneNumber),
    business_phone_number: normalizePhoneNumber(payload.businessPhoneNumber),
    bio: payload.bio,
    avatar: payload.avatarUrl ? payload.avatarUrl : null,
    is_sandbox_user: payload.isSandboxUser,
    sandbox_password: payload.sandboxPassword,
  };
}

export const addStaffService = async (
  payload: IdempotentRequest<StaffFormValues>,
) => {
  const { data } = await api<string>({
    url: API_ROUTES.STAFF,
    method: 'post',
    version: 2,
    data: createStaffFormPayload(payload),
    idempotencyKey: payload.idempotencyKey,
  });

  return data;
};

export const editStaffService = async (
  payload: IdempotentRequest<StaffFormEditValues>,
) => {
  const { data } = await api<StaffMemberDetails>({
    url: `${API_ROUTES.STAFF}${payload.uuid}`,
    method: 'put',
    version: 2,
    data: createStaffFormPayload(payload),
    idempotencyKey: payload.idempotencyKey,
  });

  return data;
};

export const deactivateStaffService = async (
  uuid: string,
  preflight: boolean,
) => {
  const { data } = await api<{ mustReplaceStaff: boolean }>({
    url: `${API_ROUTES.STAFF}${uuid}/deactivate`,
    method: 'post',
    version: 2,
    data: {
      preflight,
    },
  });

  return data;
};

export const reactivateStaffService = async (uuid: string) => {
  const { data } = await api({
    url: `${API_ROUTES.STAFF}${uuid}/reactivate`,
    method: 'post',
    version: 2,
  });

  return data;
};

interface ResetStaffPasswordProps extends UpdatePasswordFields {
  uuid: string;
}

export const resetStaffPasswordService = async (
  payload: ResetStaffPasswordProps,
) => {
  const { data } = await api({
    url: `${API_ROUTES.STAFF}${payload.uuid}/reset-password`,
    method: 'post',
    version: 2,
    data: {
      new_password: payload.password,
      repeat_new_password: payload.confirmPassword,
    },
  });

  return data;
};

export const startStaffUpdateService = async (uuid: string) => {
  const { data } = await api({
    url: `${API_ROUTES.STAFF}${uuid}/start-auth-update`,
    method: 'post',
    version: 2,
  });

  return data;
};

export const changeStaffEmailService = async (payload: UpdateEmailFields) => {
  const { data } = await api({
    url: `${API_ROUTES.STAFF}${payload.uuid}/update-email`,
    method: 'post',
    version: 2,
    data: {
      otp_code: payload.otp,
      new_email: payload.email,
      repeat_new_email: payload.confirmEmail,
    },
  });

  return data;
};

export const changeStaffPhoneService = async (payload: UpdatePhoneFields) => {
  const { data } = await api({
    url: `${API_ROUTES.STAFF}${payload.uuid}/update-phone-number`,
    method: 'post',
    version: 2,
    data: {
      otp_code: payload.otp,
      new_phone_number: payload.phone,
      repeat_new_phone_number: payload.confirmPhone,
    },
  });

  return data;
};
